import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '../../supabaseClient';
import { toast } from 'sonner';
import { 
 UserPlus, GraduationCap, Mail, 
 Phone, ShieldCheck, Zap, 
 ChevronRight, Layout, Info, Star, ChevronLeft, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AddTeacher = () => {
 const navigate = useNavigate();
 const [isPending, setIsPending] = useState(false);
 const [formData, setFormData] = useState({
  name: '',
  subject: '',
  email: '',
  phone: ''
 });

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setIsPending(true);

  try {
   // 0. Check if email already exists in Students table (Students remain globally unique)
   const { data: existingStudent } = await supabase
    .from('students')
    .select('full_name')
    .eq('email', formData.email)
    .maybeSingle();

   if (existingStudent) {
    throw new Error(`This email is already registered as a student (${existingStudent.full_name}).`);
   }

    // 1. Identity Resolution (Find existing identity or create new)
    const schoolId = localStorage.getItem('current_school_id') || '15d35319-3fd1-4684-b539-7528db0614e8';
    
    if (!schoolId) {
      throw new Error("Identity Synchronization Error: Institutional node ID is missing from your session. Please refresh the page.");
    }

    let userId: string | null = null;
    
    // Check if user already exists in our teachers table (any institution)
    const { data: globalIdentity } = await supabase
      .from('teachers')
      .select('id')
      .eq('email', formData.email)
      .maybeSingle();

    if (globalIdentity) {
      userId = globalIdentity.id;
    } else {
      // Initialize temporary client for session isolation
      const tempSupabase = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY,
        {
          auth: {
            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false
          }
        }
      );

      const { data: authData, error: authError } = await tempSupabase.auth.signUp({
        email: formData.email,
        password: 'Teacher@123',
        options: {
          data: {
            full_name: formData.name,
            role: 'teacher',
          },
        },
      });

      if (authError) {
        // Fallback: If Auth says user exists but we didn't find them in Teachers (maybe they are an Admin or Student)
        if (authError.message.includes("already registered") || authError.status === 400) {
           // We can't easily get the ID of an existing user from the client if they aren't in our DB
           // without an Edge Function, so we report a clearer error.
           throw new Error("This email is already registered in the system. Use another email or contact support.");
        }
        throw authError;
      }
      
      userId = authData.user?.id || null;
    }

    if (userId) {
      // 2. Institutional Association (Upsert)
      // Identity Resolution: Use upsert to associate with institutional node
      const { error: dbError } = await supabase.from('teachers').upsert({
        id: userId,
        full_name: formData.name,
        subject: formData.subject,
        email: formData.email,
        phone: formData.phone,
        role: 'teacher',
        school_id: schoolId
      }, { onConflict: 'id' });
      
      if (dbError) {
        console.error("Critical DB Sync Error:", dbError);
        throw new Error(`Database Synchronization Failed [${dbError.code || 'UNKNOWN'}]: ${dbError.message || 'Operation Aborted'}`);
      }
    }

    toast.success("Teacher Created Successfully!");
    navigate('/admin/dashboard');

   } catch (err: any) {
    toast.error("Error: " + err.message);
   } finally {
    setIsPending(false);
   }
  };

 return (
  <div className="min-h-screen bg-[var(--bg-main)] py-12 px-4 md:px-10 pb-32 flex items-center justify-center">
   <div className="max-w-2xl w-full">
    
    {/* --- HEADER --- */}
    <div className="mb-12 text-center space-y-4">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
       <h1 className="text-5xl md:text-6xl font-black text-slate-900  leading-none uppercase">
        Add<br/>
        <span className="text-[var(--accent-admin)]">Teacher</span>
       </h1>
       <p className="text-slate-400 font-black text-[10px] mt-4 flex items-center justify-center gap-2">
        <ShieldCheck size={12} className="text-[var(--accent-admin)]" /> Teacher Details
       </p>
      </motion.div>
    </div>

    <motion.div 
     initial={{ opacity: 0, scale: 0.95 }}
     animate={{ opacity: 1, scale: 1 }}
     className="premium-card p-10 md:p-14 relative overflow-hidden"
    >
     <div className="absolute top-0 left-0 w-full h-[8px] bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600" />
     
     <div className="flex items-center gap-6 border-b border-slate-50 pb-8 mb-10">
       <div className="w-12 h-12 bg-blue-50 rounded-[5px] flex items-center justify-center text-blue-600 shadow-inner">
        <UserPlus size={24} />
       </div>
       <div>
        <h2 className="text-2xl font-black text-slate-900  uppercase">Login Details</h2>
        <p className="text-[9px] font-black text-slate-300 tracking-widest leading-none">NEW TEACHER FORM</p>
       </div>
     </div>

     <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 gap-8">
        <InputField 
         label="Full Name *" 
         name="name" 
         placeholder="Ex: Rajesh Kumar" 
         value={formData.name}
         onChange={(e: any) => setFormData({...formData, name: e.target.value})} 
         required 
         icon={GraduationCap}
        />

        <InputField 
         label="Subject / Role *" 
         name="subject" 
         placeholder="Ex: Advanced Mathematics" 
         value={formData.subject}
         onChange={(e: any) => setFormData({...formData, subject: e.target.value})} 
         required 
         icon={Layout}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
         <InputField 
          label="Email Address *" 
          name="email" 
          type="email" 
          placeholder="teacher@asm-portal.com" 
          value={formData.email}
          onChange={(e: any) => setFormData({...formData, email: e.target.value})} 
          required 
          icon={Mail}
         />

         <InputField 
          label="Mobile Number *" 
          name="phone" 
          placeholder="+91 XXXX-XXXXXX" 
          value={formData.phone}
          onChange={(e: any) => setFormData({...formData, phone: e.target.value})} 
          required 
          icon={Phone}
         />
        </div>
      </div>

      <div className="bg-slate-50 p-6 rounded-[5px] border border-slate-100 space-y-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 blur-2xl rounded-full" />
        <div className="flex items-center gap-3 relative z-10">
         <Info size={16} className="text-blue-500" />
         <p className="text-[10px] font-black text-slate-400 tracking-widest leading-none">Login Information</p>
        </div>
        <p className="text-[11px] font-black text-slate-500 leading-relaxed relative z-10">
         A new account will be created. The default password is: <span className="text-blue-600 font-black notranslate">Teacher@123</span>. Please ask the teacher to change their password after logging in.
        </p>
      </div>

      <div className="pt-6 flex flex-col sm:flex-row gap-4">
        <button 
         type="submit"
         disabled={isPending}
         className="premium-button-admin flex-1"
        >
         {isPending ? (
          <RefreshCw className="animate-spin" size={20} />
         ) : (
          <><Zap size={20} className="group-hover:translate-y-[-2px] transition-transform" /> Save Teacher </>
         )}
        </button>
        
        <button 
         type="button"
         onClick={() => navigate('/admin/dashboard')}
         className="px-10 py-6 rounded-[5px] font-black  text-[10px] text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all"
        >
         Cancel
        </button>
      </div>
     </form>
    </motion.div>

    {/* --- FOOTER DECOR --- */}
    <div className="mt-12 text-center group cursor-default">
      <div className="inline-flex items-center gap-3 bg-white px-6 py-2.5 rounded-full border border-slate-100 shadow-sm transition-all group-hover:scale-105">
       <Star size={14} className="text-amber-400 fill-amber-400" />
       <p className="text-[9px] font-black text-slate-400 tracking-widest">Teacher Portal</p>
      </div>
    </div>
   </div>
  </div>
 );
};

const InputField = ({ label, icon: Icon, ...props }: any) => (
 <div className="space-y-1 group">
  <label className="block text-[9px] font-black text-slate-400  ml-2 transition-colors group-focus-within:text-blue-500">{label}</label>
  <div className="relative">
   {Icon && <Icon className="absolute left-8 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within/input:text-blue-400 transition-colors" size={18} />}
   <input className="premium-input pl-16" {...props} />
  </div>
 </div>
);

export default AddTeacher;
